<template>
  <div
    :id="`vendor-assessment-question-heading-${index}`"
    :key="index"
    :class="`tw-flex tw-flex-row tw-overflow-hidden tw-items-center tw-font-[Poppins] tw-pr-[25px] assessment-heading-container`"
  >
    <span
      class="tw-cursor-pointer drag-handle tw-pl-[15px] tw-pr-[5px] tw-flex tw-items-center tw-py-[15px]"
      :data-cy="`drag-handle-${index}`"
    >
      <DraggableIcon :width="8" :height="12" />
    </span>
    <CustomCheckbox
      :class="`!tw-h-[20px] !tw-mb-0 !tw-pl-0 tw-ml-[5px] tw-mr-[15px]`"
      size="20px"
      :is-checked="isSelected"
      @update:is-checked="
        (value) => {
          emits('handle-select-item', value, index);
        }
      "
    />
    <input
      :id="`input-vendor-question-${index}`"
      v-model="form.heading"
      :data-cy="`input-heading-name-${index}`"
      :data-question-name="`${form.heading}`"
      :class="`tw-relative tw-w-full tw-mr-[15px] tw-placeholder-[#B7B7B7] tw-ml-[15px] tw-text-[18px] tw-font-semibold tw-font-[Poppins] tw-text-[#211F58] input-detail-question`"
      :placeholder="
        getComponentUiText(
          'app-assessment-assessment-vendor-assessment-heading-placeholder-1',
        )
      "
      :disabled="item.itemMasterId === -1"
      style="border: unset; background-color: unset"
      @blur="isFocus = false"
      @focus="
        (val: any) => {
          currentValue = val.target.value;
          isFocus = true;
        }
      "
      @focusout="
        (val: any) => {
          if (currentValue !== val.target.value) {
            handleUpdateAssessmentQuestionName(val.target.value);
          }
        }
      "
      @update:model-value="
        (text) => {
          heading = text;
          form.heading = text;
          handleUpdateData(text);
        }
      "
    />
    <div
      v-if="isCallAPI"
      class="tw-h-[38px] tw-w-[38px] tw-justify-end tw-right-[25px] tw-flex tw-items-center"
    >
      <!-- eslint-disable vue/no-unused-refs -->
      <LottieAnimation
        ref="anim"
        :animation-data="ProgressAnimation"
        :auto-play="true"
        :loop="true"
      />
    </div>
    <div
      v-if="isSuccess"
      class="tw-h-[38px] tw-w-[38px] tw-justify-end tw-right-[25px] tw-flex tw-items-center"
    >
      <!-- eslint-disable vue/no-unused-refs -->
      <LottieAnimation
        ref="anim"
        :animation-data="SuccessAnimation"
        :auto-play="true"
        :loop="true"
      />
    </div>

    <div
      v-if="isFail"
      class="tw-h-[38px] tw-w-[38px] tw-justify-end tw-right-[25px] tw-flex tw-items-center"
    >
      <!-- eslint-disable vue/no-unused-refs -->
      <LottieAnimation
        ref="anim"
        :animation-data="ErrorAnimation"
        :auto-play="true"
        :loop="true"
      />
    </div>
    <ButtonShowMore
      v-if="item.itemMasterId !== -1"
      :id-popover="`popover-vendor-assessment-heading-${index}${
        item.itemMasterId ?? Math.random()
      }`"
      :is-from-notification="true"
      :data-cy-id="`popover-vendor-assessment-question-${index}`"
      :data-question-id="`${item.itemMasterId}`"
    >
      <CustomDropDownItem
        v-if="item?.itemMasterId !== -1"
        :style="'danger'"
        :id-text="'app-assessment-vendor-delete-heading-title-1'"
        :style-text="'!tw-self-auto'"
        data-cy="remove-question-menu"
        :id-item="`popover-vendor-assessment-heading-item-${index}-${
          item.itemMasterId ?? Math.random()
        }`"
        @click="emits('removeStep', index)"
      />
    </ButtonShowMore>
  </div>
  <b-form-invalid-feedback v-if="v$.heading.$error" :class="'is-invalid'">
    <VRuntimeTemplate :template="v$.heading?.$errors?.[0]?.$message" />
  </b-form-invalid-feedback>
</template>
<script setup lang="ts">
import { getComponentUiText } from "@/utils/utilities";
import { computed, onMounted, ref, watch } from "vue";
import DraggableIcon from "@/components/svgs/draggableIcon.vue";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import CustomCheckbox from "../common/customCheckBox.vue";
import VRuntimeTemplate from "vue3-runtime-template";
import ButtonShowMore from "../common/setting/buttonShowMore.vue";
import CustomDropDownItem from "../common/customDropDownItem.vue";
import { updateChecklistRequestedTemplateItem } from "@/services/vendor";
import store from "@/store";
import { Vendor } from "@/services/apiRoutes/routes";
import { showErrorAPI } from "@/services/apiRoutes";
import { LottieAnimation } from "lottie-web-vue";
import ProgressAnimation from "../../assets/animation/progress_light.json";
import SuccessAnimation from "../../assets/animation/success.json";
import ErrorAnimation from "../../assets/animation/error.json";
import { VENDOR_TEMPLATE_API_STATUS } from "@/utils/constants";
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    required: false,
    default: "",
  },
  index: {
    type: Number,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
  templateId: {
    type: [String, Number, null],
    required: true,
  },
  isSelected: {
    type: Boolean,
    required: false,
    default: false,
  },
});
const isFocus = ref(false);
const isCallAPI = ref(false);
const heading = ref(
  props.item.itemMasterHeadingLabel || props.item.itemMasterName || "",
);

const isSuccess = ref(false);
const isFail = ref(false);

const currentValue = ref("");

const margin = computed(() => {
  return `${props.level * 25}px`;
});

const handleUpdateAssessmentQuestionName = async (val) => {
  if (props.item.itemMasterId) {
    await v$.value.$validate();
    if (v$.value.$error) return;
    try {
      isCallAPI.value = true;
      await updateChecklistRequestedTemplateItem({
        templateId: props.templateId,
        params: {
          itemMasterId: props.item.itemMasterId,
          isHeading: true,
          headingLabel: val,
          name: val,
        },
      });
      isSuccess.value = true;
      setTimeout(() => {
        isSuccess.value = false;
      }, 2000);
    } catch (error) {
      isFail.value = true;
      setTimeout(() => {
        isFail.value = false;
      }, 1500);
      form.value.heading = currentValue.value;
      showErrorAPI(error, Vendor.putChecklistRequestedTemplateItem);
    } finally {
      isCallAPI.value = false;
    }
  }
};

const emits = defineEmits([
  "changeExpand",
  "removeStep",
  "handleUpdateData",
  "handle-select-item",
]);
const handleUpdateData = (text) => {
  heading.value = text;
  emits("handleUpdateData", heading.value, props.index);
};
const rules = {
  heading: {
    required: helpers.withMessage(
      () => getComponentUiText("app-setting-org-detail-required-fields-1"),
      required,
    ),
  },
};

const form = ref({
  heading: heading.value,
});

const statusCallAPIPOST = computed(() => {
  const addTemplateItemStatus = store.state.Vendor.addTemplateItemStatus;
  return addTemplateItemStatus.find((x) => x.id === props.item?.id);
});

watch(statusCallAPIPOST, (value) => {
  if (value?.loading === VENDOR_TEMPLATE_API_STATUS.isloading) {
    isCallAPI.value = true;
  } else {
    isCallAPI.value = false;
  }
  if (value?.loading === VENDOR_TEMPLATE_API_STATUS.suceess) {
    isSuccess.value = true;
    setTimeout(() => {
      isSuccess.value = false;
      store.dispatch("Vendor/setAddTemplateItemStatus", {
        id: props.item.id,
        loading: VENDOR_TEMPLATE_API_STATUS.remove,
      });
    }, 2000);
  }
  if (value?.loading === VENDOR_TEMPLATE_API_STATUS.error) {
    isFail.value = true;
    setTimeout(() => {
      isFail.value = false;
      store.dispatch("Vendor/setAddTemplateItemStatus", {
        id: props.item.id,
        loading: VENDOR_TEMPLATE_API_STATUS.remove,
      });
    }, 1500);
  }
});

const v$ = useVuelidate(rules, form as any);

onMounted(() => {
  if (
    statusCallAPIPOST.value?.loading === VENDOR_TEMPLATE_API_STATUS.isloading
  ) {
    isCallAPI.value = true;
  } else {
    isCallAPI.value = false;
  }
  if (statusCallAPIPOST.value?.loading === VENDOR_TEMPLATE_API_STATUS.suceess) {
    isSuccess.value = true;
    setTimeout(() => {
      isSuccess.value = false;
      store.dispatch("Vendor/setAddTemplateItemStatus", {
        id: props.item.id,
        loading: VENDOR_TEMPLATE_API_STATUS.remove,
      });
    }, 2000);
  }
  if (statusCallAPIPOST.value?.loading === VENDOR_TEMPLATE_API_STATUS.error) {
    isFail.value = true;
    setTimeout(() => {
      store.dispatch("Vendor/setAddTemplateItemStatus", {
        id: props.item.id,
        loading: VENDOR_TEMPLATE_API_STATUS.remove,
      });
      isFail.value = false;
    }, 1500);
  }
  document.getElementById(`input-vendor-question-${props.index}`)?.focus();
});

const saveChanges = async () => {
  await v$.value.$validate();
  // true is not error
  return !v$.value.$error;
};
defineExpose({
  saveChanges,
});
</script>
<style lang="css" scoped>
.is-invalid {
  margin-left: 15px;

  &::after {
    content: "";
  }
}
.detail-question {
  border-top: 1px solid #eff0f1;
}
.assessment-heading-container {
  margin-left: v-bind(margin);
  /* border: 1px solid #f7553c;
  background: #fee5e2;
  height: 64px;
  border-radius: 6px; */
}
</style>
